import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LayoutDashboard, PhoneCall, CalendarCheck, Inbox as InboxIcon,
  Settings as SettingsIcon, CreditCard, LogOut, Sparkles, AlertTriangle, Clock, MessageSquare,
  Bot, Plug, Bell, ListChecks, CalendarClock, Users, Phone, Wallet, CalendarDays, Link2,
} from "lucide-react";
import Overview from "@/components/dashboard/Overview";
import CallLogs from "@/components/dashboard/CallLogs";
import Appointments from "@/components/dashboard/Appointments";
import Staff from "@/components/dashboard/Staff";
import ImpersonationBanner from "@/components/dashboard/ImpersonationBanner";
import Inbox from "@/components/dashboard/Inbox";
import Settings from "@/components/dashboard/Settings";
import Billing from "@/components/dashboard/Billing";
import ContactMessages from "@/components/dashboard/ContactMessages";
import MyReceptionist from "@/components/dashboard/MyReceptionist";
import Integrations from "@/components/dashboard/Integrations";
import ServicesPricing from "@/components/dashboard/ServicesPricing";
import Calendar from "@/components/dashboard/Calendar";
import PhoneSetup from "@/components/dashboard/PhoneSetup";
import Payments from "@/components/dashboard/Payments";
import BookingSystems from "@/components/dashboard/BookingSystems";
import ActionLinks from "@/components/dashboard/ActionLinks";
import NotificationCentre from "@/components/dashboard/NotificationCentre";
import NotificationBell from "@/components/dashboard/NotificationBell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const baseNav = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "ai-receptionist", label: "My AI Receptionist", icon: Bot },
  { key: "services", label: "Services & Pricing", icon: ListChecks },
  { key: "staff", label: "Staff", icon: Users },
  { key: "phone-number", label: "Phone Number", icon: Phone },
  { key: "calls", label: "Call Logs", icon: PhoneCall },
  { key: "appointments", label: "Appointments", icon: CalendarCheck },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "action-links", label: "Action Links", icon: Link2 },
  { key: "calendar", label: "Calendar", icon: CalendarClock },
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "booking-systems", label: "Booking Systems", icon: CalendarDays },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "settings", label: "AI Settings", icon: SettingsIcon },
  { key: "billing", label: "Billing", icon: CreditCard },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { view = "overview" } = useParams();
  const { user, logout } = useAuth();
  const [sub, setSub] = useState(null);

  const nav = user?.role === "admin"
    ? [...baseNav, { key: "contact", label: "Contact", icon: MessageSquare }]
    : baseNav;

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  if (user && user.role === "staff") return <Navigate to="/staff-portal" replace />;

  const render = () => {
    switch (view) {
      case "ai-receptionist": return <MyReceptionist />;
      case "services": return <ServicesPricing />;
      case "staff": return <Staff />;
      case "phone-number": return <PhoneSetup />;
      case "calls": return <CallLogs />;
      case "appointments": return <Appointments />;
      case "payments": return <Payments />;
      case "action-links": return <ActionLinks />;
      case "calendar": return <Calendar />;
      case "inbox": return <Inbox />;
      case "integrations": return <Integrations />;
      case "booking-systems": return <BookingSystems />;
      case "notifications": return <NotificationCentre />;
      case "settings": return <Settings />;
      case "billing": return <Billing />;
      case "contact": return <ContactMessages />;
      default: return <Overview onNavigate={(v) => navigate(`/dashboard/${v}`)} />;
    }
  };

  const doLogout = () => { logout(); navigate("/"); };

  const banner = () => {
    if (!sub) return null;
    if (sub.status === "trial_expired" || sub.status === "canceled") {
      return (
        <div data-testid="dash-paused-banner" className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-center gap-3 mb-6">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 flex-1">Your AI receptionist is <b>paused</b>. Subscribe to resume answering calls — your data is safe.</p>
          <button onClick={() => navigate("/dashboard/billing")} className="text-sm font-semibold text-red-700 hover:underline shrink-0">Go to Billing →</button>
        </div>
      );
    }
    if (sub.status === "none") {
      return (
        <div data-testid="dash-setup-banner" className="rounded-xl border border-blue-200 bg-blue-50 p-3 flex items-center gap-3 mb-6">
          <Sparkles className="h-4 w-4 text-blue-600 shrink-0" />
          <p className="text-sm text-blue-700 flex-1">Test your AI receptionist and activate it to start your free trial.</p>
          <button onClick={() => navigate("/dashboard/ai-receptionist")} className="text-sm font-semibold text-blue-700 hover:underline shrink-0">Go to My AI Receptionist →</button>
        </div>
      );
    }
    if (sub.status === "trialing" && sub.trial_days_left != null) {
      return (
        <div data-testid="dash-trial-banner" className="rounded-xl border border-blue-200 bg-blue-50 p-3 flex items-center gap-3 mb-6">
          <Clock className="h-4 w-4 text-blue-600 shrink-0" />
          <p className="text-sm text-blue-700 flex-1"><b>{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"}</b> left in your free trial.</p>
          <button onClick={() => navigate("/dashboard/billing")} className="text-sm font-semibold text-blue-700 hover:underline shrink-0">Subscribe →</button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-200 bg-white hidden md:flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-1.5 px-6 border-b border-slate-100">
          <img src="/operamind-mark.png" alt="OperaMind" className="h-10 w-10 object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
            <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => {
            const active = view === n.key;
            return (
              <button
                key={n.key}
                data-testid={`nav-${n.key}`}
                onClick={() => navigate(`/dashboard/${n.key}`)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 flex items-center gap-1 px-2 py-2 overflow-x-auto">
        {nav.map((n) => (
          <button key={n.key} data-testid={`mnav-${n.key}`} onClick={() => navigate(`/dashboard/${n.key}`)}
            className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold ${view === n.key ? "bg-slate-900 text-white" : "text-slate-500"}`}>
            {n.label}
          </button>
        ))}
        <button data-testid="mnav-logout" onClick={doLogout} className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold text-red-500 flex items-center gap-1">
          <LogOut className="h-3.5 w-3.5" /> Out
        </button>
      </div>

      <main className="relative flex-1 md:ml-64 pt-16 md:pt-0 overflow-hidden">
        <div className="glow-blob glow-blue glow-float h-80 w-80 -top-10 right-10 opacity-30" />
        <div className="glow-blob glow-sky glow-float h-72 w-72 top-1/2 -left-10 opacity-25" style={{ animationDelay: "4s" }} />
        <ImpersonationBanner />
        <div className="hidden md:block fixed top-4 right-6 z-30">
          <NotificationBell onOpenCentre={() => navigate("/dashboard/notifications")} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-8">
          {banner()}
          {render()}
        </div>
      </main>
    </div>
  );
}
